import { useState, useEffect, useRef } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import { formatDate } from '../../utils/format';
import ContractorPanel from '../admin/ContractorPanel';
import ApproversPanel from '../admin/ApproversPanel';
import AddContractorModal from '../admin/AddContractorModal';

// Cross-worker contractor overview: active/inactive contractors, days used vs
// remaining per contract, timesheets awaiting approval, and contracts nearing
// completion (near end date or low on days).
//
// It is also where contractors are managed. The External Resources page was a
// second list of the same people; it folded in here on 27 October 2026. A
// contractor's name opens their whole record (details, contracts, onboarding,
// timesheets, access log, comments) in a side drawer, a supplier's name opens
// that supplier's contractors, and the Approvers view and Add Contractor sit
// in the header. All of that is for holders of `resource-workers` (External
// Resources in Roles & Permissions), which is what the server asks of every
// one of those calls; anyone else with the dashboard sees the table only.

const STATUS_CLS = {
  active:    'bg-green-100 text-green-700',
  issued:    'bg-blue-100 text-blue-700',
  draft:     'bg-gray-100 text-gray-500',
  completed: 'bg-purple-100 text-purple-600',
  cancelled: 'bg-red-100 text-red-400',
};

function Tile({ label, value, sub, tone }) {
  const toneCls = tone === 'amber' ? 'text-amber-600' : tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-emerald-600' : 'text-navy';
  return (
    <div className="bg-white border border-gray-200 rounded p-2.5">
      <div className="text-[8px] text-gray-400 uppercase font-bold">{label}</div>
      <div className={`text-xl font-extrabold ${toneCls}`}>{value}</div>
      {sub && <div className="text-[8px] text-gray-400">{sub}</div>}
    </div>
  );
}

// Same shape as the Tracker's SortHeader: click the heading to sort, type in
// the box under it to filter that column (commits on Enter, clears on Escape).
// Defined outside the page component so React keeps the input mounted (and
// focused) across parent re-renders.
function SortHeader({ col, label, sortCol, sortDir, toggleSort, colFilter, setColFilter }) {
  const active = sortCol === col;
  const [val, setVal] = useState(colFilter[col] || '');

  // The "clear filters" button empties colFilter behind this input's back;
  // without this the box would keep showing a filter that no longer applies.
  useEffect(() => { setVal(colFilter[col] || ''); }, [colFilter[col]]);

  const commit = (v) => setColFilter(p => ({ ...p, [col]: v }));

  return (
    <th
      className="px-3 py-1.5 text-left text-[8px] uppercase font-bold border-b bg-gray-50 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
      onClick={() => toggleSort(col)}
    >
      <span className={active ? 'text-gold' : 'text-gray-500'}>
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </span>
      <div className="mt-0.5">
        <input
          type="text"
          value={val}
          onChange={e => { e.stopPropagation(); setVal(e.target.value); }}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Enter') commit(val);
            if (e.key === 'Escape') { setVal(''); commit(''); }
          }}
          placeholder="filter… ↵"
          className="w-full min-w-[60px] text-[7px] border border-gray-200 rounded px-1 py-0 font-normal normal-case tracking-normal text-gray-600 bg-white focus:outline-none focus:border-gold"
        />
      </div>
    </th>
  );
}

// The right-hand drawer both the contractor and the supplier open in. The
// table stays in view beside it, dimmed, and a click on the dimmed part closes
// it. Escape closes it too, unless a dialog opened from inside the drawer is
// showing, in which case Escape is that dialog's.
function SideDrawer({ label, wide, onClose, children }) {
  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const dialogs = [...document.querySelectorAll('[role="dialog"]')].filter(d => d !== panelRef.current);
      if (dialogs.length === 0) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label}
        className={`fixed top-0 right-0 bottom-0 w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} z-50 bg-white shadow-2xl flex flex-col border-l-4 border-gold`}>
        {children}
      </div>
    </>
  );
}

const qaState = (sup) => !sup ? null
  : sup.qa_expired > 0 ? 'expired'
  : sup.qa_verified >= (sup.qa_doc_count || 0) && sup.qa_doc_count > 0 ? 'ok'
  : sup.qa_doc_count > 0 ? 'partial' : 'none';
const QA_CLS = { ok: 'bg-green-100 text-green-700', expired: 'bg-red-100 text-red-600', partial: 'bg-amber-100 text-amber-700', none: 'bg-gray-100 text-gray-500' };
const QA_MSG = { ok: 'All QA documents verified', expired: 'QA documents expired: action required', partial: 'QA documents partially complete', none: 'No QA documents on file' };

// A supplier and the contractors who work through them. What the External
// Resources list showed as a supplier group, with each contractor's live
// contracts from the dashboard rows. `workers` is the full contractor list for
// managers (so one with no contract yet still shows); without it the
// contractors are the ones the dashboard rows name.
function SupplierDrawerBody({ supplierId, supplierName, supplier, rows, workers, canManage, onOpenContractor, onAdd, onClose }) {
  const { navigate } = useApp() || {};
  const supRows = rows.filter(r => r.supplier_id === supplierId && !r.no_contract);
  const people = workers
    ? workers.filter(w => w.supplier_id === supplierId).map(w => ({ id: w.id, name: `${w.first_name} ${w.last_name}`.trim(), active: !!w.is_active, pending: w.pending_timesheets || 0 }))
    : [...new Map(supRows.map(r => [r.resource_worker_id, { id: r.resource_worker_id, name: r.worker_name, active: !!r.worker_active, pending: r.ts_awaiting || 0 }])).values()];
  people.sort((a, b) => (b.active - a.active) || a.name.localeCompare(b.name));
  const live = supRows.filter(r => r.status === 'active');
  const remaining = live.reduce((n, r) => n + (+r.remaining || 0), 0);
  const pending = people.reduce((n, p) => n + p.pending, 0);
  const qa = qaState(supplier);

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[8px] uppercase font-bold text-gray-400">Supplier</div>
            <div className="text-[13px] font-bold text-navy">{supplier?.name || supplierName}</div>
            {qa && (
              <div className={`inline-flex items-center gap-1 mt-1 text-[8px] font-semibold px-1.5 py-0.5 rounded ${QA_CLS[qa]}`}>
                {QA_MSG[qa]}
                {supplier.qa_doc_count > 0 && <span className="opacity-70">({supplier.qa_verified}/{supplier.qa_doc_count})</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-6 h-6 rounded-full border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 flex items-center justify-center text-[10px] flex-shrink-0">✕</button>
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          {canManage && (
            <button onClick={onAdd} className="text-[9px] px-2 py-1 bg-navy text-white rounded font-semibold hover:bg-navy/90">+ Add contractor</button>
          )}
          {navigate && canAccess('suppliers') && (
            <button onClick={() => navigate('suppliers', supplierId)} className="text-[9px] px-2 py-1 bg-gold/10 text-gold hover:bg-gold/20 rounded font-semibold">Supplier record and QA →</button>
          )}
        </div>
      </div>

      <div className="p-4 flex-1 min-h-0 overflow-y-auto space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <Tile label="Active contracts" value={live.length} />
          <Tile label="Days remaining" value={Math.round(remaining * 100) / 100} tone="green" />
          <Tile label="Timesheets to approve" value={pending} tone={pending ? 'amber' : undefined} />
        </div>

        {supplier && (supplier.contact_name || supplier.contact_email || supplier.contact_phone || supplier.company_number || supplier.vat_number || supplier.address) && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-[9px] text-gray-600 space-y-0.5">
            {supplier.contact_name && <div><span className="text-gray-400">Contact:</span> {supplier.contact_name}</div>}
            {supplier.contact_email && <div><span className="text-gray-400">Email:</span> {supplier.contact_email}</div>}
            {supplier.contact_phone && <div><span className="text-gray-400">Phone:</span> {supplier.contact_phone}</div>}
            {supplier.company_number && <div><span className="text-gray-400">Co. No:</span> {supplier.company_number}</div>}
            {supplier.vat_number && <div><span className="text-gray-400">VAT:</span> {supplier.vat_number}</div>}
            {supplier.address && <div className="text-gray-400 whitespace-pre-line pt-1">{supplier.address}</div>}
          </div>
        )}

        <div>
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Contractors ({people.length})</div>
          {people.length === 0 && <div className="text-[9px] text-gray-400 italic">No contractors with this supplier.</div>}
          <div className="border border-gray-200 rounded divide-y divide-gray-100">
            {people.map(p => {
              const theirs = supRows.filter(r => r.resource_worker_id === p.id && r.status === 'active');
              const Name = canManage ? 'button' : 'div';
              return (
                <div key={p.id} className={`px-3 py-2 ${p.active ? '' : 'opacity-60'}`}>
                  <div className="flex items-center gap-1.5">
                    <Name {...(canManage ? { onClick: () => onOpenContractor(p.id) } : {})}
                      className={`text-[10px] font-semibold text-left ${canManage ? 'text-navy hover:underline' : 'text-gray-800'}`}>{p.name}</Name>
                    {!p.active && <span className="text-[7px] uppercase font-bold bg-gray-200 text-gray-500 px-1 py-0.5 rounded">Inactive</span>}
                    {p.pending > 0 && <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">{p.pending} to approve</span>}
                  </div>
                  {theirs.length === 0 && <div className="text-[8px] text-gray-400 mt-0.5">No active contract</div>}
                  {theirs.map(r => (
                    <div key={r.id} className="flex items-center gap-3 mt-1">
                      <span className="text-[8px] font-mono text-navy w-24 truncate" title={r.reference}>{r.reference || '—'}</span>
                      <span className="text-[8px] text-blue-600 font-semibold w-20 truncate">{r.sow_reference || (r.overhead_category ? 'Overhead' : '—')}</span>
                      <DaysBar used={r.consumed} agreed={r.agreed_days} pct={r.pct_used} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function DaysBar({ used, agreed, pct }) {
  const p = pct != null ? pct : (agreed > 0 ? Math.min(100, Math.round((used / agreed) * 100)) : 0);
  const cls = p >= 90 ? 'bg-red-500' : p >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="w-28">
      <div className="h-1.5 bg-gray-100 rounded overflow-hidden"><div className={`h-full ${cls}`} style={{ width: `${p}%` }} /></div>
      <div className="text-[8px] text-gray-500 mt-0.5">{used} / {agreed || '—'} days{pct != null ? ` · ${pct}%` : ''}</div>
    </div>
  );
}

export default function ContractorDashboardPage() {
  const { detailId, navigate } = useApp() || {};
  const canManage = canAccess('resource-workers');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('contracts'); // contracts | approvers
  const [filter, setFilter] = useState('active'); // active | nearing | inactive | none | all
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState(null); // null = the API's order (active first, then name)
  const [sortDir, setSortDir] = useState('asc');
  const [colFilter, setColFilter] = useState({});

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // For managers: every contractor (so one with no contract yet still has a
  // row to open) and every supplier (names, QA and contact details).
  const [workers, setWorkers] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  // What the drawer shows: { kind: 'contractor', id, tab } or { kind: 'supplier', id, name }
  const [drawer, setDrawer] = useState(null);
  // Add Contractor dialog: null when shut, otherwise the supplier to preselect ('' for none)
  const [addFor, setAddFor] = useState(null);

  const load = () => {
    api.get('resource-dashboard')
      .then(setData)
      .catch(() => setData({ contracts: [], summary: {} }))
      .finally(() => setLoading(false));
    if (canManage) {
      api.get('resource-workers').then(w => setWorkers(w || [])).catch(() => setWorkers([]));
      api.get('suppliers').then(sp => setSuppliers(sp || [])).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openContractor = (id, tab) => setDrawer({ kind: 'contractor', id, tab: tab || 'info' });
  const openSupplier = (id, name) => setDrawer({ kind: 'supplier', id, name });
  const closeDrawer = () => {
    setDrawer(null);
    // Arrived on a link to one contractor: drop the id from the address too,
    // or following the same link again would change nothing and open nothing.
    if (detailId && navigate) navigate('contractor-dashboard', null);
  };

  // A link to a contractor (#resource-workers/<id> from search or a SOW's
  // External Resources list, which now land here) opens their drawer. The
  // contract-ending notification links with the contract's id, so a contract
  // id opens the contractor who holds it.
  useEffect(() => {
    if (!detailId || !canManage || !data || !workers) return;
    if (workers.some(w => w.id === detailId)) { openContractor(detailId); return; }
    const c = (data.contracts || []).find(x => x.id === detailId);
    if (c) openContractor(c.resource_worker_id, 'contracts');
  }, [detailId, data, workers]); // eslint-disable-line

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;
  // A contractor with no contract has no dashboard row, which would leave
  // somebody just added with nowhere to be opened from. Managers get a row for
  // each of them, under the No contract filter (and All, and Inactive).
  const contracts = [
    ...(data?.contracts || []),
    ...((workers || []).filter(w => !(w.contract_count > 0)).map(w => ({
      id: `w:${w.id}`, no_contract: true, resource_worker_id: w.id,
      worker_name: `${w.first_name} ${w.last_name}`.trim(), worker_active: !!w.is_active,
      supplier_id: w.supplier_id || null, supplier_name: w.supplier_name || suppliers.find(sp => sp.id === w.supplier_id)?.name || null,
      ts_awaiting: w.pending_timesheets || 0,
    }))),
  ];
  const noContractCount = contracts.filter(c => c.no_contract).length;
  const s = data?.summary || {};

  // What each column sorts and filters on. Cells that render a bar or a badge
  // (days, timesheets) expose the number behind them so "highest to lowest"
  // compares 9 < 10 rather than '9' > '10'; cells that render two values
  // (contract + role, start – end) expose both so the filter matches what the
  // eye can see.
  const cellVal = (c, col) => {
    switch (col) {
      case 'contractor': return c.worker_name || '';
      case 'supplier':   return c.supplier_name || '';
      case 'contract':   return `${c.reference || ''} ${c.role_description || ''}`.trim();
      case 'project':    return c.sow_reference || (c.overhead_category ? 'Overhead' : '');
      case 'status':     return c.status || '';
      case 'term':       return `${c.start_date || ''} ${c.end_date || ''}`.trim();
      case 'days':       return c.pct_used != null ? c.pct_used : (c.agreed_days > 0 ? Math.min(100, Math.round((c.consumed / c.agreed_days) * 100)) : 0);
      case 'timesheets': return c.ts_awaiting || 0;
      default:           return c[col];
    }
  };

  let filtered = contracts.filter(c => {
    // Item 38. `status` is the CONTRACT's status and `worker_active` is the
    // person's — so a contractor who had been made inactive kept appearing
    // under Active on the strength of a contract nobody had closed. Active now
    // means both: an active contract held by an active worker.
    if (filter === 'active' && !(c.status === 'active' && c.worker_active)) return false;
    if (filter === 'nearing' && !(c.status === 'active' && c.worker_active && c.nearing_completion)) return false;
    if (filter === 'inactive' && c.worker_active) return false;
    if (filter === 'none' && !c.no_contract) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${c.worker_name} ${c.supplier_name || ''} ${c.reference || ''} ${c.sow_reference || ''} ${c.role_description || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Column filters: case-insensitive substring on what the column shows.
  for (const [col, val] of Object.entries(colFilter)) {
    if (!val?.trim()) continue;
    const v = val.trim().toLowerCase();
    filtered = filtered.filter(c => String(cellVal(c, col) ?? '').toLowerCase().includes(v));
  }

  // Sort, once a heading has been clicked; numbers numerically, text A–Z.
  if (sortCol) {
    filtered = [...filtered].sort((a, b) => {
      const av = cellVal(a, sortCol) ?? '';
      const bv = cellVal(b, sortCol) ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const shProps = { sortCol, sortDir, toggleSort, colFilter, setColFilter };
  const openId = drawer?.kind === 'contractor' ? drawer.id : null;

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Contractor Dashboard</h2>
          <div className="text-[9px] text-gray-500">
            External resource contracts — days used vs remaining, timesheet status and contracts nearing completion.
            {canManage && ' Click a contractor or a supplier to open it. Contractor portal at /resource, approver portal at /client-portal.'}
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-[9px]">
              <button onClick={() => setView('contracts')} className={`px-3 py-1.5 font-semibold transition-colors ${view === 'contracts' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Contracts</button>
              <button onClick={() => setView('approvers')} className={`px-3 py-1.5 font-semibold transition-colors border-l border-gray-200 ${view === 'approvers' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Approvers</button>
            </div>
            <button onClick={() => setAddFor('')} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ Add Contractor</button>
          </div>
        )}
      </div>

      {view === 'approvers' && <ApproversPanel onOpenContractor={id => openContractor(id)} />}

      {view === 'contracts' && <>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        <Tile label="Active contractors" value={s.workers_active ?? 0} />
        <Tile label="Inactive" value={s.workers_inactive ?? 0} />
        <Tile label="Active contracts" value={s.active_contracts ?? 0} />
        <Tile label="Nearing completion" value={s.nearing_completion ?? 0} tone={s.nearing_completion ? 'amber' : undefined} />
        <Tile label="Timesheets to approve" value={s.timesheets_awaiting ?? 0} tone={s.timesheets_awaiting ? 'amber' : undefined} />
        <Tile label="Days used" value={s.days_used ?? 0} sub={`of ${s.days_agreed ?? 0} agreed`} />
        <Tile label="Days remaining" value={s.days_remaining ?? 0} tone="green" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-2">
        {[['active', 'Active'], ['nearing', 'Nearing completion'], ['inactive', 'Inactive'], ...(noContractCount ? [['none', 'No contract']] : []), ['all', 'All']].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-2.5 py-1 text-[9px] font-semibold rounded border transition-all ${filter === k ? 'border-navy bg-navy/5 text-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {lbl}{k === 'nearing' && s.nearing_completion ? ` (${s.nearing_completion})` : ''}{k === 'none' ? ` (${noContractCount})` : ''}
          </button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search contractor, supplier, contract, SOW…"
          className="form-input text-[9px] py-1 flex-1 min-w-[200px] max-w-[360px]" />
        {Object.values(colFilter).some(v => v?.trim()) && (
          <button onClick={() => setColFilter({})}
            className="px-2 py-1 text-[9px] font-semibold bg-amber-50 border border-amber-300 text-amber-700 rounded hover:bg-amber-100 whitespace-nowrap">
            ✕ Clear column filters
          </button>
        )}
        <span className="text-[8px] text-gray-400 ml-auto">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <SortHeader col="contractor" label="Contractor" {...shProps} />
            <SortHeader col="supplier" label="Supplier" {...shProps} />
            <SortHeader col="contract" label="Contract" {...shProps} />
            <SortHeader col="project" label="Project" {...shProps} />
            <SortHeader col="status" label="Status" {...shProps} />
            <SortHeader col="term" label="Term" {...shProps} />
            <SortHeader col="days" label="Days used / remaining" {...shProps} />
            <SortHeader col="timesheets" label="Timesheets" {...shProps} />
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[10px] text-gray-400">No contracts.</td></tr>}
            {filtered.map(c => (
              <tr key={c.id}
                onClick={canManage ? () => openContractor(c.resource_worker_id) : undefined}
                className={`border-b border-gray-100 hover:bg-gray-50 ${canManage ? 'cursor-pointer' : ''} ${openId === c.resource_worker_id ? 'bg-navy/5' : c.nearing_completion ? 'bg-amber-50/40' : ''} ${!c.worker_active ? 'opacity-60' : ''}`}>
                <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-800 whitespace-nowrap">
                  {canManage
                    ? <button onClick={e => { e.stopPropagation(); openContractor(c.resource_worker_id); }} className="font-semibold text-navy hover:underline">{c.worker_name}</button>
                    : c.worker_name}
                  {!c.worker_active && <span className="ml-1 text-[7px] uppercase font-bold bg-gray-200 text-gray-500 px-1 py-0.5 rounded">Inactive</span>}
                </td>
                <td className="px-3 py-1.5 text-[9px] text-gray-600">
                  {c.supplier_id
                    ? <button onClick={e => { e.stopPropagation(); openSupplier(c.supplier_id, c.supplier_name); }} className="text-left text-gray-600 hover:text-navy hover:underline">{c.supplier_name || 'Supplier'}</button>
                    : canManage
                      ? <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="Link one from the contractor's Info tab">No supplier</span>
                      : '—'}
                </td>
                <td className="px-3 py-1.5 text-[9px]">
                  {c.no_contract
                    ? <span className="text-[8px] text-gray-400 italic">No contract yet</span>
                    : <>
                        <div className="font-mono text-navy">{c.reference || '—'}</div>
                        {c.role_description && <div className="text-[8px] text-gray-400 max-w-[160px] truncate" title={c.role_description}>{c.role_description}</div>}
                      </>}
                </td>
                <td className="px-3 py-1.5 text-[9px] text-blue-600 font-semibold whitespace-nowrap">{c.sow_reference || (c.overhead_category ? 'Overhead' : '—')}</td>
                <td className="px-3 py-1.5">{c.no_contract ? <span className="text-gray-300 text-[9px]">—</span> : <span className={`text-[7px] px-1.5 py-0.5 rounded font-bold uppercase ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-500'}`}>{c.status || '—'}</span>}</td>
                <td className="px-3 py-1.5 text-[8px] text-gray-500 whitespace-nowrap">
                  {c.start_date ? formatDate(c.start_date) : '—'}{c.end_date ? ` – ${formatDate(c.end_date)}` : ''}
                  {c.days_to_end != null && c.status === 'active' && (
                    <div className={`font-semibold ${c.days_to_end < 0 ? 'text-red-600' : c.days_to_end <= 30 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {c.days_to_end < 0 ? `${Math.abs(c.days_to_end)}d overdue` : `${c.days_to_end}d left`}
                    </div>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {c.no_contract ? <span className="text-gray-300 text-[9px]">—</span> : <>
                    <DaysBar used={c.consumed} agreed={c.agreed_days} pct={c.pct_used} />
                    <div className={`text-[8px] font-semibold ${c.low_days ? 'text-red-600' : 'text-emerald-600'}`}>{c.remaining} remaining</div>
                  </>}
                </td>
                <td className="px-3 py-1.5 text-[9px] text-center">
                  {c.ts_awaiting > 0
                    ? <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{c.ts_awaiting} to approve</span>
                    : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {drawer?.kind === 'contractor' && (
        <SideDrawer wide label="Contractor" onClose={closeDrawer}>
          <ContractorPanel
            workerId={drawer.id}
            initialTab={drawer.tab}
            suppliers={suppliers}
            onSuppliersChanged={setSuppliers}
            onChanged={load}
            onDeleted={() => { closeDrawer(); load(); }}
            onClose={closeDrawer}
          />
        </SideDrawer>
      )}

      {drawer?.kind === 'supplier' && (
        <SideDrawer label="Supplier" onClose={closeDrawer}>
          <SupplierDrawerBody
            supplierId={drawer.id}
            supplierName={drawer.name}
            supplier={suppliers.find(sp => sp.id === drawer.id)}
            rows={contracts}
            workers={canManage ? workers : null}
            canManage={canManage}
            onOpenContractor={id => openContractor(id)}
            onAdd={() => setAddFor(drawer.id)}
            onClose={closeDrawer}
          />
        </SideDrawer>
      )}

      {addFor !== null && (
        <AddContractorModal
          supplierId={addFor}
          suppliers={suppliers}
          onSuppliersChanged={setSuppliers}
          onClose={() => setAddFor(null)}
          onCreated={id => { setAddFor(null); load(); if (id) openContractor(id, 'contracts'); }}
        />
      )}
    </div>
  );
}
